// ---------------------------------------------------------------------------
// Identidad para analítica y monitoreo, enganchada al store de usuario.
//
// Por qué aquí y no dentro de un componente de React:
//
// Hay cinco caminos distintos por los que se cierra sesión — `logoutUser`,
// `clearSession`, `forceLogout`, `forceRemoteLogout` y el canal
// BroadcastChannel("auth") entre pestañas — y TODOS terminan poniendo
// `state.user = null` en el store de zustand. Suscribirse una vez al store
// cubre los cinco sin tocar ninguno, y cubre también cualquier sexto camino que
// alguien agregue en el futuro.
//
// Eso importa más de lo que parece: si no se llama a `posthog.reset()` al
// cerrar sesión, en una máquina compartida (caseta, despacho, cambio de turno)
// el siguiente usuario hereda el distinct_id del anterior y PostHog fusiona a
// las dos personas de forma permanente. No se puede deshacer.
// ---------------------------------------------------------------------------
import { useUserStore } from "../store/userStore";
import { identifyPostHog, isPostHogIdentified, resetPostHog } from "../posthog";
import { setUser as setSentryUser } from "../sentry";
import {
  clearAnalyticsUser,
  setAnalyticsUserId,
  setAnalyticsUserProperties,
} from "../utils/analytics";

/**
 * Huella de la última identidad aplicada, para no reenviar `identify` en cada
 * render ni en el doble montaje de StrictMode.
 */
let lastFingerprint: string | null = null;

/** Baja de la suscripción activa, si ya hay una. */
let unsubscribe: (() => void) | null = null;

function applyIdentity(user: Record<string, unknown> | null): void {
  const rawId = user?.id;
  const id = rawId === undefined || rawId === null ? null : String(rawId);

  if (!id) {
    // Se consulta al SDK ADEMAS de la huella en memoria. La huella sola no
    // alcanza: `clearSession` llega a `setUser(null)` recién después de dos
    // llamadas axios y de `signOut`, y se invoca sin await desde
    // useSessionCheck. Si la pestaña se recarga antes de que eso resuelva, el
    // reset nunca corre; en la carga siguiente la huella arranca en null y un
    // guard que solo la mirara se saltaria el reset otra vez, dejando al
    // siguiente usuario con el distinct_id del anterior. La identidad de
    // PostHog vive en localStorage+cookie y sí sobrevive a la recarga.
    if (lastFingerprint !== null || isPostHogIdentified()) {
      lastFingerprint = null;
      resetPostHog();
      setSentryUser(null);
      // Firebase Analytics también conserva el user_id y las propiedades de
      // usuario; sin esto, en un equipo compartido todo lo que hiciera el
      // siguiente usuario antes de entrar seguiría atribuido al anterior.
      clearAnalyticsUser();
    }
    return;
  }

  // Las llamadas a /role y /company en pages/login/functions.ts están dentro de
  // try/catch y pueden fallar sin tumbar el login, así que nada de esto está
  // garantizado. De ahí los respaldos desde localStorage.
  //
  // Los respaldos de localStorage solo se usan si pertenecen a ESTE usuario.
  // `clearSession` sí las borra (van en AUTH_STORAGE_KEYS), pero aun así puede
  // quedar un valor viejo: si el /role del usuario B falla, sin esta
  // comprobación B se identificaría con el rol del usuario A en PostHog,
  // Sentry y GA4 a la vez.
  const storedRoleId = localStorage.getItem("userRoleId") || undefined;
  const ownRoleId = user?.role_id as string | undefined;
  const staleRole = Boolean(ownRoleId && storedRoleId && ownRoleId !== storedRoleId);

  const roleName = staleRole
    ? undefined
    : localStorage.getItem("userRole") || undefined;
  const roleId = ownRoleId || (staleRole ? undefined : storedRoleId);
  const companyId =
    (user?.company_id as string | undefined) ||
    localStorage.getItem("userCompanyId") ||
    undefined;
  const companyName = localStorage.getItem("userCompanyName") || undefined;

  const email = (user?.email as string | undefined)?.trim() || undefined;

  const fingerprint = [id, email, companyId, companyName, roleName, roleId].join(
    "|",
  );
  if (fingerprint === lastFingerprint) return;
  lastFingerprint = fingerprint;

  const nombre =
    [user?.first_name, user?.last_name]
      .filter((p): p is string => typeof p === "string" && p.trim() !== "")
      .join(" ") || undefined;

  identifyPostHog({
    id,
    companyId,
    companyName,
    roleName,
    roleId,
    yardId: user?.yard_id as string | undefined,
    // El correo va SIEMPRE: es como el equipo reconoce a una persona en
    // PostHog, y sin el la ficha es un uid ilegible y no se puede responder
    // "que hizo fulano", que es justo para lo que se instalo esto.
    //
    // Ojo con lo que NO cambia: el distinct_id sigue siendo `user.id` (el id
    // de Firebase), no el correo. Un correo se puede cambiar, y si fuera la
    // llave ese cambio partiria el historial de la persona en dos. Con el id
    // como llave y el correo como propiedad se tienen las dos cosas.
    email,
    // Ademas del correo completo, porque permite segmentar por cuenta de
    // cliente ("todo lo de @clientex.com") sin escribir el correo entero.
    emailDomain: email?.split("@")[1],
    name: nombre,
  });

  // sentry.ts exporta setUser desde siempre pero nadie lo llamaba: hasta hoy
  // todos los errores de Sentry eran anónimos.
  setSentryUser({ id, username: roleName });

  // Firebase Analytics sigue igual que antes.
  setAnalyticsUserId(id);
  setAnalyticsUserProperties({
    company_id: companyId,
    company_name: companyName,
    // Antes esto mandaba `user.role`, que no existe en el usuario del store
    // (el campo es `role_id`), así que user_role siempre viajaba undefined.
    user_role: roleName,
  });
}

/**
 * Arranca la sincronización de identidad. Se llama una vez desde main.tsx,
 * fuera de React: así sobrevive al doble montaje de StrictMode y al HMR, y lee
 * al usuario ya rehidratado por el middleware `persist` del store.
 */
export function startAnalyticsIdentitySync(): void {
  // Idempotente: en HMR este módulo se re-ejecuta y, sin esto, cada recarga en
  // caliente dejaría una suscripción extra viva y cada cambio de usuario
  // dispararía un identify/reset por cada una.
  unsubscribe?.();
  applyIdentity(useUserStore.getState().user);
  unsubscribe = useUserStore.subscribe((state) => applyIdentity(state.user));
}

/** Solo para tests. */
export function _resetIdentityStateForTests(): void {
  unsubscribe?.();
  unsubscribe = null;
  lastFingerprint = null;
}
